/**
 * Google Analytics 4 + Google Ads (gtag.js) helper.
 *
 * Both the GA4 property `G-MG5CG0ED1W` and the Ads account `AW-575238149`
 * share a single gtag.js loader in index.html. Page views are tracked
 * manually via the SPA router (see usePageTracking) — automatic `page_view`
 * is disabled on both so SPA navigations fire exactly one page_view per
 * property per route change.
 */

type GtagArgs =
    | ["js", Date]
    | ["config", string, Record<string, unknown>?]
    | ["set", Record<string, unknown>]
    | ["event", string, Record<string, unknown>?];

declare global {
    interface Window {
        dataLayer: unknown[];
        gtag: (...args: GtagArgs) => void;
    }
}

export const GA_MEASUREMENT_ID = "G-MG5CG0ED1W";
export const GOOGLE_ADS_ID = "AW-575238149";

const isEnabled = () =>
    typeof window !== "undefined" && typeof window.gtag === "function";

const emit = (name: string, params: Record<string, unknown> = {}) => {
    if (!isEnabled()) return;
    window.gtag("event", name, params);
};

export const trackPageView = (path: string, title?: string) => {
    if (!isEnabled()) return;
    const params = {
        page_path: path,
        page_title: title ?? document.title,
        page_location: window.location.href,
    };
    // Fire the page_view for both the GA4 property and the Ads account so
    // SPA route changes feed Ads conversions / remarketing audiences the
    // same way they feed GA4 reports.
    window.gtag("config", GA_MEASUREMENT_ID, params);
    window.gtag("config", GOOGLE_ADS_ID, params);
};

export type ContactChannel =
    | "whatsapp_sy"
    | "whatsapp_tr"
    | "phone"
    | "email"
    | "telegram"
    | "map";

export const trackContactClick = (
    channel: ContactChannel,
    location: string,
) => emit("contact_click", { channel, location });

export const trackCtaClick = (cta: string, location: string) =>
    emit("cta_click", { cta, location });

export const trackFormSubmit = (
    form: string,
    status: "success" | "error" | "validation_error",
    extra: Record<string, unknown> = {},
) => emit("form_submit", { form, status, ...extra });

export const trackProjectClick = (
    name: string,
    url: string,
    location: string,
) => emit("project_click", { project_name: name, project_url: url, location });

export const trackVideoPlay = (title: string, videoId?: string) =>
    emit("video_play", { video_title: title, video_id: videoId ?? "" });

export const trackOutboundLink = (url: string, label?: string) =>
    emit("outbound_click", { url, label: label ?? "" });

export const trackSectionAnchor = (section: string, location: string) =>
    emit("section_anchor_click", { section, location });

export const trackSocialClick = (network: string, location: string) =>
    emit("social_click", { network, location });

export const trackChatOpen = (location: string) =>
    emit("chat_open", { location });

/**
 * Tag every subsequent GA4 event with the active UI language so reports can
 * segment by `language=ar` / `language=en` without extra parameters per event.
 */
export const setAnalyticsLanguage = (language: "ar" | "en") => {
    if (!isEnabled()) return;
    window.gtag("set", { language });
};
